import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core'
import { useEffect } from 'react'
import { ScanDetails } from '../../../../shared/src/data/scanDetails'
import { useGetFilesRelatedToScan } from './queries/scanMetadata'
import { Alert, AlertTitle } from '@material-ui/lab'
import { FileDetails } from '../../../../shared/src/data/fileDetails'

type FilesRelatedToScanProps = {
  scan?: ScanDetails
  fileDetails?: FileDetails[]
  setFileDetails: (files?: FileDetails[]) => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alertContainer: {
      width: '100%',
    },
    loading: {
      marginTop: theme.spacing(1),
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  }),
)

export default function FilesRelatedToScan({
  scan,
  fileDetails,
  setFileDetails,
}: FilesRelatedToScanProps): JSX.Element {
  const {
    data: relatedFileDetails,
    isLoading: loadingRelatedFiles,
    error: fetchingRelatedFilesError,
    refetch,
  } = useGetFilesRelatedToScan(scan?.uploadId)

  useEffect(
    () => setFileDetails(relatedFileDetails),
    [relatedFileDetails, setFileDetails],
  )
  const classes = useStyles()

  const relatedFilesErrorMessage =
    (fetchingRelatedFilesError as Error)?.message || 'Unknown error'

  return (
    <Box>
      {!scan ? (
        <></>
      ) : !scan.uploadId ? (
        <Typography variant='h6'>Scan has no related files</Typography>
      ) : loadingRelatedFiles ? (
        <CircularProgress className={classes.loading} color='secondary' />
      ) : fetchingRelatedFilesError ? (
        <Alert
          severity='error'
          className={classes.alertContainer}
          action={
            <Button
              variant='contained'
              color='secondary'
              onClick={() => refetch()}
            >
              RETRY
            </Button>
          }
        >
          <AlertTitle>
            <Typography variant='h6'>Failed to get related files</Typography>
          </AlertTitle>
          <Typography variant='subtitle1'>
            Unable to get related files: "{relatedFilesErrorMessage}"
          </Typography>
        </Alert>
      ) : fileDetails?.length ? (
        <div>
          <List
            disablePadding
            subheader={
              <ListSubheader disableGutters>
                <Typography variant='h6'>Files related to scan</Typography>
              </ListSubheader>
            }
          >
            {fileDetails.map((f, index) => (
              <ListItem key={f.id}>
                <ListItemText primary={f.name} />
              </ListItem>
            ))}
          </List>
        </div>
      ) : (
        <Typography variant='h6'>No files were attached to the scan</Typography>
      )}
    </Box>
  )
}
